import { alpha } from '@mui/material/styles';
import { brand, red } from '@theme/primitives';

// What the menus and the record dialogs float on: a surface lighter than the page, lifted by a
// deep shadow.
export const floatingSurface = {
  backgroundColor: '#1E2027',
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: alpha('#fff', 0.08),
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.35)',
};

/** The lines between a record dialog's header, body and footer, and around a list inside it. */
export const dialogLine = alpha('#fff', 0.08);

/** The footer's tray, a shade darker than the dialog, and a well that holds a list inside it. */
export const dialogTray = '#191B21';

/** The frame of what can be pressed or chosen in a record dialog: Cancel, a role's card. */
export const dialogFrame = alpha('#fff', 0.16);

/**
 * A dialog that creates or edits a record (a folder, a user), or asks before one is deleted, laid
 * out as a CRM lays out its create and edit dialogs: a header with the record's icon and a close
 * button, each field under its label, and a footer tray with Cancel beside the filled primary
 * action. It floats on the menus' surface.
 */
export const formDialogPaperStyles = {
  ...floatingSurface,
  width: 440,
  maxWidth: 'calc(100% - 32px)',
  m: 2,
  p: 0,
  borderRadius: '12px',
};

export const formDialogHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  pl: 2.5,
  pr: 1.5,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: dialogLine,
};

export const formDialogTitleStyles = {
  flex: 1,
  minWidth: 0,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
};

export const formDialogCloseStyles = {
  width: 32,
  height: 32,
  borderRadius: '8px',
  border: 'none',
  color: 'text.secondary',
  backgroundColor: 'transparent',
  '&:hover': {
    color: 'text.primary',
    backgroundColor: alpha('#fff', 0.06),
  },
};

/** The header's mark: the record's icon, or what the dialog asks about, in a round of its colour. */
export const formDialogMarkStyles = (color: string) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  color,
  backgroundColor: alpha(color, 0.14),
});

export const formDialogBodyStyles = {
  px: 2.5,
  pt: 2.5,
  pb: 1.5,
};

/** What a dialog that asks says, under its header. */
export const formDialogTextStyles = {
  fontSize: 14,
  lineHeight: '22px',
  color: 'text.secondary',
};

/** The field's name over it, quiet, with the red mark of a required field. */
export const formDialogLabelStyles = {
  display: 'block',
  mb: 0.75,
  fontSize: 13,
  fontWeight: 500,
  color: 'text.secondary',
  '& .MuiFormLabel-asterisk': {
    color: red[300],
  },
};

/**
 * MUI's own outlined field, as the move dialog has it, with the corners of the dialog's buttons
 * and its error under the label's line. At 16px on a phone, where a smaller text makes Safari
 * zoom in on focus.
 */
export const formDialogFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: { xs: 16, sm: 14 },
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
    mt: 0.75,
    fontSize: 12.5,
    lineHeight: '18px',
  },
  // An error in the red of the required mark: the theme's own is too dark to read on this surface.
  '& .MuiFormHelperText-root.Mui-error': {
    color: red[300],
  },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: red[300],
  },
};

/** The footer tray, a shade darker than the dialog, with the actions at its end. */
export const formDialogFooterStyles = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1,
  px: 2.5,
  py: 1.5,
  borderTop: '1px solid',
  borderColor: dialogLine,
  backgroundColor: dialogTray,
};

const formDialogButtonStyles = {
  height: 32,
  minWidth: 72,
  px: 2,
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 500,
};

export const formDialogCancelStyles = {
  ...formDialogButtonStyles,
  color: 'text.primary',
  border: '1px solid',
  borderColor: dialogFrame,
  backgroundColor: 'transparent',
  '&:hover': {
    borderColor: alpha('#fff', 0.24),
    backgroundColor: alpha('#fff', 0.06),
  },
  '&.Mui-disabled': {
    borderColor: alpha('#fff', 0.08),
  },
};

// The page's blue, a step darker so its white label keeps a contrast of 4.5 to 1.
export const formDialogSubmitStyles = {
  ...formDialogButtonStyles,
  color: '#fff',
  backgroundColor: brand[500],
  '&:hover': {
    backgroundColor: brand[700],
  },
  '&.Mui-disabled': {
    color: alpha('#fff', 0.35),
    backgroundColor: alpha('#fff', 0.08),
  },
};

// For what cannot be taken back: red, dark enough for its white label to keep 4.5 to 1.
export const formDialogDangerStyles = {
  ...formDialogButtonStyles,
  color: '#fff',
  backgroundColor: red[400],
  '&:hover': {
    backgroundColor: red[500],
  },
  '&.Mui-disabled': {
    color: alpha('#fff', 0.35),
    backgroundColor: alpha('#fff', 0.08),
  },
};
